import { SpinResponse } from '@cgs/data-access';
import { Container } from '@cgs/wge';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { GameStateMachine } from '../../reels_engine/state_machine/game-state-machine';
import { BaseGameState } from '../../reels_engine/state_machine/states/base_game_state';
import { GameStateMachineStates } from '../../reels_engine/state_machine/game-state-machine-states';

export interface AbstractListener {
  onStateEntered?(slotState: string): void;
  onStateExited?(slotState: string): void;
}

export abstract class SlotNotifier {
  abstract addListener(listener: AbstractListener): void;
  abstract removeListener(listener: AbstractListener): void;
  abstract notifyListenersEntered(slotState: string): void;
  abstract notifyListenersExited(slotState: string): void;
}

export class GameStateMachineNotifierComponent {
  public readonly notifier: GameStateMachineNotifier;
  public readonly gameStateMachine: GameStateMachine<SpinResponse>;

  constructor(container: Container) {
    this.notifier = new GameStateMachineNotifier();
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    const states = this.gameStateMachine.rootState.states
      .map((entry) => entry.state as BaseGameState<SpinResponse>)
      .filter((state) => !!state);

    states.forEach((state) => {
      state.entered.listen(() => {
        this.notifier.notifyListenersEntered(state.id);
      });
      state.leaved.listen(() => {
        this.notifier.notifyListenersExited(state.id);
      });
    });
  }
}

export class GameStateMachineNotifier extends SlotNotifier {
  private _listeners: AbstractListener[] = [];

  public addListener(listener: AbstractListener) {
    this._listeners.push(listener);
  }

  public removeListener(listener: AbstractListener) {
    const index = this._listeners.indexOf(listener);
    if (index !== -1) {
      this._listeners.splice(index, 1);
    }
  }

  public notifyListenersEntered(slotState: string) {
    for (const listener of this._listeners) {
      listener.onStateEntered?.(slotState);
    }
  }

  public notifyListenersExited(slotState: string) {
    for (const listener of this._listeners) {
      listener.onStateExited?.(slotState);
    }
  }
}

export class StateMachineListener implements AbstractListener {
  private stateMachine: GameStateMachine<SpinResponse>;

  constructor(container: Container) {
    this.stateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
  }

  public onStateEntered(slotState: string) {
    switch (slotState) {
      case GameStateMachineStates.Accelerate:
        this.onAccelerateEntered(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.Bonus:
        this.onBonusEntered(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.EndBonus:
        this.onEndBonusEntered(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.Scatter:
        this.onScatterEntered(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.Stop:
        this.onStopEntered(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.BeginFreeSpins:
        this.onBeginFreeSpinsEntered(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.FreeSpinRecovery:
        this.onFreeSpinRecoveryEntered(
          this.stateMachine.curResponse,
          this.stateMachine.prevResponse
        );
        break;
      case GameStateMachineStates.ImmediatelyStop:
        this.onImmediatelyStopEntered(
          this.stateMachine.curResponse,
          this.stateMachine.prevResponse
        );
        break;
      case GameStateMachineStates.InitGame:
        this.onInitEntered(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.WaitRequest:
        this.onWaitRequestEntered(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.EndOfFreeSpinsPopup:
        this.onEndFreeSpinsPopupEntered(
          this.stateMachine.curResponse,
          this.stateMachine.prevResponse
        );
        break;
      case GameStateMachineStates.BeginFreeSpinsPopup:
        this.onBeginFreeSpinsPopupEntered(
          this.stateMachine.curResponse,
          this.stateMachine.prevResponse
        );
        break;
    }
  }

  public onStateExited(slotState: string) {
    switch (slotState) {
      case GameStateMachineStates.Bonus:
        this.onBonusExited(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.Animation:
        this.onAnimationExited(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.EndScatter:
        this.onEndScatterExited(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.WaitRequest:
        this.onWaitRequestExited(this.stateMachine.curResponse, this.stateMachine.prevResponse);
        break;
      case GameStateMachineStates.EndOfFreeSpinsPopup:
        this.onEndFreeSpinsPopupExited(
          this.stateMachine.curResponse,
          this.stateMachine.prevResponse
        );
        break;
    }
  }

  public onInitEntered(_currentResponse: SpinResponse, _previousResponse: SpinResponse | null) {
    // to override
  }

  public onAccelerateEntered(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onImmediatelyStopEntered(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onWaitRequestEntered(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onWaitRequestExited(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onEndFreeSpinsPopupEntered(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onEndFreeSpinsPopupExited(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onBonusEntered(_currentResponse: SpinResponse, _previousResponse: SpinResponse | null) {
    // to override
  }

  public onEndBonusEntered(_currentResponse: SpinResponse, _previousResponse: SpinResponse | null) {
    // to override
  }

  public onScatterEntered(_currentResponse: SpinResponse, _previousResponse: SpinResponse | null) {
    // to override
  }

  public onStopEntered(_currentResponse: SpinResponse, _previousResponse: SpinResponse | null) {
    // to override
  }

  public onBeginFreeSpinsEntered(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onFreeSpinRecoveryEntered(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onBeginFreeSpinsPopupEntered(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }

  public onAnimationExited(_currentResponse: SpinResponse, _previousResponse: SpinResponse | null) {
    // to override
  }

  public onBonusExited(_currentResponse: SpinResponse, _previousResponse: SpinResponse | null) {
    // to override
  }

  public onEndScatterExited(
    _currentResponse: SpinResponse,
    _previousResponse: SpinResponse | null
  ) {
    // to override
  }
}
